import { useEffect } from 'react';
import { useLinkClickHandler } from 'react-router-dom';
import { Button, Text } from '@teachstonellc/component-library';
import ReportLoader from 'components/Reports/ReportLoader';
import i18n from 'helpers/i18n';
import useAsync from 'hooks/useAsync';
import { getTransitionTestReports } from 'actions/trainings';
import { TransitionTestReports } from 'types/api/ReliabilityReport';
import ReliabilitySummary from './ReliabilitySummary';
import messages from './messages';
import {
  Section,
  Container,
  Header,
  MobileHeader,
  Actions,
  MobileActions,
  ErrorSection,
  StyledAnchor
} from './Styled';

export default function TransitionReport({
  trainingId,
  hasTests = true
}: {
  trainingId: number;
  hasTests?: boolean;
}) {
  const reportPath = `/reports/transition/${trainingId}`;
  const onReportClick = useLinkClickHandler<HTMLAnchorElement>(reportPath);
  const { run, data, isPending, isError } = useAsync<TransitionTestReports>();

  useEffect(() => {
    if (hasTests) run(getTransitionTestReports(trainingId));
  }, [run, trainingId, hasTests]);

  if (hasTests && isPending) {
    return <ReportLoader label={i18n.ft(messages.loading)} />;
  }
  if (!hasTests || isError) {
    return (
      <ErrorSection role="alert">
        {i18n.ft(messages.errorMessage)}{' '}
        <StyledAnchor href="mailto:contact@teachstone.com">
          contact@teachstone.com
        </StyledAnchor>
      </ErrorSection>
    );
  }

  const title = (
    <Text as="h2" className="font-bold text-blue-600 text-lg">
      {i18n.ft(messages.headerCombined)}
    </Text>
  );
  const reportLink = (
    <Button
      as="a"
      href={reportPath}
      onClick={onReportClick}
      size="small"
      shape="rounded"
    >
      {i18n.ft(messages.viewCombinedReport)}
    </Button>
  );

  return (
    <Section>
      <Container>
        <MobileHeader>{title}</MobileHeader>
        <Header>
          {title}
          <Actions>{reportLink}</Actions>
        </Header>
        <ReliabilitySummary
          passed={data.reliability.total_passed}
          failed={data.reliability.total_taken - data.reliability.total_passed}
          color="#00779E"
          label={i18n.ft(messages.reliabilityLabel)}
        />
        <ReliabilitySummary
          testType="knowledge"
          passed={data.knowledge.total_passed}
          failed={data.knowledge.total_taken - data.knowledge.total_passed}
          color="#00856A"
          label={i18n.ft(messages.knowledgeLabel)}
        />
        <MobileActions>{reportLink}</MobileActions>
      </Container>
    </Section>
  );
}
